import {SketchRelationKind} from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import {SketchPointKind} from '../../mesh/sketch/ISketchObject';
import {SketchDimension} from '../dimension/model/SketchDimension';
import type {SketchDimensionSnapshot} from '../dimension/SketchDimensionTypes';
import type {SketchPersistentCircularPatternRecord} from '../../../types/sketch/circular-pattern/SketchCircularPatternTypes';
import type {
    SketchMirrorAxisReference,
    SketchPersistentMirrorAxisReferenceRecord,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import {
    SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1,
    type SketchPersistentArcRecord,
    type SketchPersistentCircleRecord,
    type SketchPersistentCollectionRecord,
    type SketchPersistentDocument,
    type SketchPersistentEllipseRecord,
    type SketchPersistentEntityRecord,
    type SketchPersistentLineRecord,
    type SketchPersistentParabolaRecord,
    type SketchPersistentPlainData,
    type SketchPersistentPointRecord,
    type SketchPersistentReferencePlaneRecord,
    type SketchPersistentRelationRecord,
    type SketchPersistentSlotRecord,
    type SketchPersistentSplineMetadataRecord,
    type SketchPersistentSplineRecord,
    type SketchPersistentVector3,
} from './SketchPersistenceTypes';

/**
 * 草图持久化文档解析与校验。
 *
 * 设计说明：
 * 1. 与 `.puppy` 基准扩展同范式：逐字段断言并携带精确 JSON 路径，解析失败直接抛错，不做兜底；
 * 2. 交叉引用完整性在解析阶段收口：实体引用的点、关系引用的目标必须都能在文档内解析；
 * 3. 未知实体 kind、未知关系 kind 显式失败，禁止静默丢弃，避免版本演进时产生不可恢复文档。
 */

/** 合法关系类型值集合。 */
const RELATION_KIND_VALUES = new Set<string>(Object.values(SketchRelationKind));

/** v1 早期文档使用的共圆心同半径旧标识。 */
const LEGACY_CONGRUENT_RELATION_KIND = 'Congruent';

/**
 * 把持久化关系标识迁移为当前唯一领域类型。
 *
 * @param relationKind 文档中的关系类型字符串。
 * @param path 当前字段 JSON 路径。
 * @returns 当前领域可识别的关系类型。
 * @throws 当关系类型未知时抛出可定位错误。
 */
function normalizePersistentRelationKind(relationKind: string, path: string): SketchRelationKind {
    if (relationKind === LEGACY_CONGRUENT_RELATION_KIND) {
        return SketchRelationKind.Coradial;
    }
    if (!RELATION_KIND_VALUES.has(relationKind)) {
        throw new Error(`草图持久化文档存在未知关系类型: ${path}=${relationKind}`);
    }
    return relationKind as SketchRelationKind;
}

/** 合法点类型值集合。 */
const POINT_KIND_VALUES = new Set<string>(Object.values(SketchPointKind));

/**
 * 判断未知值是否为普通记录对象。
 * @param value 未知值。
 * @returns 是否为可读记录对象。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
    return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 断言字段是非空字符串。
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertNonEmptyString(value: unknown, path: string): asserts value is string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new Error(`草图持久化文档字段必须是非空字符串: ${path}`);
    }
}

/**
 * 断言字段是有限数字。
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertFiniteNumber(value: unknown, path: string): asserts value is number {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw new Error(`草图持久化文档字段必须是有限数字: ${path}`);
    }
}

/**
 * 断言字段是布尔值。
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertBoolean(value: unknown, path: string): asserts value is boolean {
    if (typeof value !== 'boolean') {
        throw new Error(`草图持久化文档字段必须是布尔值: ${path}`);
    }
}

/**
 * 断言字段是三维有限数字向量。
 * @param value 字段值。
 * @param path 字段路径。
 */
function assertVector3Tuple(value: unknown, path: string): asserts value is SketchPersistentVector3 {
    if (
        !Array.isArray(value)
        || value.length !== 3
        || value.some((item) => typeof item !== 'number' || !Number.isFinite(item))
    ) {
        throw new Error(`草图持久化文档字段必须是三维有限数字向量: ${path}`);
    }
}

/**
 * 校验参考平面记录。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 已校验的参考平面记录。
 */
function parseReferencePlaneRecord(value: unknown, path: string): SketchPersistentReferencePlaneRecord {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档参考平面必须是对象: ${path}`);
    }
    assertFiniteNumber(value.referencePlaneId, `${path}.referencePlaneId`);
    assertNonEmptyString(value.displayName, `${path}.displayName`);
    assertVector3Tuple(value.normal, `${path}.normal`);
    assertFiniteNumber(value.constant, `${path}.constant`);
    assertVector3Tuple(value.xAxis, `${path}.xAxis`);
    assertVector3Tuple(value.yAxis, `${path}.yAxis`);

    let hostFace: SketchPersistentReferencePlaneRecord['hostFace'] = null;
    if (value.hostFace !== null && value.hostFace !== undefined) {
        if (!isRecord(value.hostFace)) {
            throw new Error(`草图持久化文档宿主面必须是对象或 null: ${path}.hostFace`);
        }
        assertNonEmptyString(value.hostFace.objectId, `${path}.hostFace.objectId`);
        assertFiniteNumber(value.hostFace.faceIndex, `${path}.hostFace.faceIndex`);
        hostFace = {
            objectId: value.hostFace.objectId,
            faceIndex: value.hostFace.faceIndex,
        };
    }

    return {
        referencePlaneId: value.referencePlaneId,
        displayName: value.displayName,
        normal: [...value.normal] as unknown as SketchPersistentVector3,
        constant: value.constant,
        xAxis: [...value.xAxis] as unknown as SketchPersistentVector3,
        yAxis: [...value.yAxis] as unknown as SketchPersistentVector3,
        hostFace,
    };
}

/**
 * 判断值是否为可持久化普通 JSON 数据。
 * @param value 未知值。
 * @returns 是否合法。
 */
function isSerializablePlainData(value: unknown): value is SketchPersistentPlainData {
    if (value === null || typeof value === 'string' || typeof value === 'boolean') {
        return true;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (Array.isArray(value)) {
        return value.every(isSerializablePlainData);
    }
    if (!isRecord(value)) {
        return false;
    }
    return Object.values(value).every(isSerializablePlainData);
}

/**
 * 校验并深拷贝透传 JSON 载荷。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 深拷贝后的合法载荷。
 */
function parsePlainData(value: unknown, path: string): SketchPersistentPlainData {
    if (value === undefined) {
        return null;
    }
    if (!isSerializablePlainData(value)) {
        throw new Error(`草图持久化文档字段必须是可序列化 JSON: ${path}`);
    }
    return value === null ? null : JSON.parse(JSON.stringify(value)) as SketchPersistentPlainData;
}

/**
 * 校验三元组列表。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 已拷贝的三元组列表。
 */
function parseVector3TupleList(value: unknown, path: string): SketchPersistentVector3[] {
    if (!Array.isArray(value)) {
        throw new Error(`草图持久化文档字段必须是数组: ${path}`);
    }
    return value.map((item, index) => {
        assertVector3Tuple(item, `${path}[${index}]`);
        return [...item] as unknown as SketchPersistentVector3;
    });
}

/**
 * 校验 Worker 导出的权威 B 样条求值定义。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 已校验并深拷贝的求值定义。
 */
export function parseSketchSplineEvaluationDefinition(
    value: unknown,
    path: string,
): SketchBSplineEvaluationDefinition {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档样条求值定义必须是对象: ${path}`);
    }
    if (!Array.isArray(value.controlPoints) || value.controlPoints.length < 2) {
        throw new Error(`草图持久化文档样条求值极点至少需要 2 个: ${path}.controlPoints`);
    }
    const controlPoints = value.controlPoints.map((point, index) => {
        if (!isRecord(point)) {
            throw new Error(`草图持久化文档样条求值极点必须是对象: ${path}.controlPoints[${index}]`);
        }
        assertFiniteNumber(point.x, `${path}.controlPoints[${index}].x`);
        assertFiniteNumber(point.y, `${path}.controlPoints[${index}].y`);
        assertFiniteNumber(point.z, `${path}.controlPoints[${index}].z`);
        return {x: point.x, y: point.y, z: point.z};
    });
    if (!Array.isArray(value.weights) || value.weights.length !== controlPoints.length) {
        throw new Error(`草图持久化文档样条求值权重数量必须与极点一致: ${path}.weights`);
    }
    const weights = value.weights.map((weight, index) => {
        assertFiniteNumber(weight, `${path}.weights[${index}]`);
        return weight;
    });
    if (!Array.isArray(value.knots) || value.knots.length < controlPoints.length + 2) {
        throw new Error(`草图持久化文档样条求值节点数量不足: ${path}.knots`);
    }
    const knots = value.knots.map((knot, index) => {
        assertFiniteNumber(knot, `${path}.knots[${index}]`);
        return knot;
    });
    assertFiniteNumber(value.degree, `${path}.degree`);
    assertFiniteNumber(value.firstParameter, `${path}.firstParameter`);
    assertFiniteNumber(value.lastParameter, `${path}.lastParameter`);
    assertBoolean(value.periodic, `${path}.periodic`);
    return {
        controlPoints,
        weights,
        degree: value.degree,
        knots,
        firstParameter: value.firstParameter,
        lastParameter: value.lastParameter,
        periodic: value.periodic,
    };
}

/**
 * 校验单条点记录。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 已校验的点记录。
 */
function parsePointRecord(value: unknown, path: string): SketchPersistentPointRecord {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档点记录必须是对象: ${path}`);
    }
    assertNonEmptyString(value.pointId, `${path}.pointId`);
    assertVector3Tuple(value.position, `${path}.position`);
    assertNonEmptyString(value.pointKind, `${path}.pointKind`);
    if (!POINT_KIND_VALUES.has(value.pointKind)) {
        throw new Error(`草图持久化文档点类型无效: ${path}.pointKind=${value.pointKind}`);
    }
    if (value.boxSelectionRole !== undefined) {
        assertNonEmptyString(value.boxSelectionRole, `${path}.boxSelectionRole`);
    }
    if (value.isAuxiliaryObject !== undefined) {
        assertBoolean(value.isAuxiliaryObject, `${path}.isAuxiliaryObject`);
    }
    return {
        pointId: value.pointId,
        position: [...value.position] as unknown as SketchPersistentVector3,
        pointKind: value.pointKind,
        ...(value.boxSelectionRole !== undefined ? {boxSelectionRole: value.boxSelectionRole as string} : {}),
        ...(value.isAuxiliaryObject === true ? {isAuxiliaryObject: true} : {}),
    };
}

/**
 * 校验单条实体记录。
 * @param value 字段值。
 * @param path 字段路径。
 * @param pointIdSet 点池 ID 集合（交叉引用校验）。
 * @returns 已校验的实体记录。
 */
function parseEntityRecord(
    value: unknown,
    path: string,
    pointIdSet: ReadonlySet<string>,
): SketchPersistentEntityRecord {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档实体记录必须是对象: ${path}`);
    }
    assertNonEmptyString(value.entityId, `${path}.entityId`);
    assertNonEmptyString(value.kind, `${path}.kind`);

    const assertPoolPointId = (pointId: unknown, fieldPath: string): string => {
        assertNonEmptyString(pointId, fieldPath);
        if (!pointIdSet.has(pointId)) {
            throw new Error(`草图持久化文档实体引用了点池中不存在的点: ${fieldPath}=${pointId}`);
        }
        return pointId;
    };

    if (value.kind === 'line') {
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        assertBoolean(value.isInfiniteLength, `${path}.isInfiniteLength`);
        if (value.isCenterline !== undefined) {
            assertBoolean(value.isCenterline, `${path}.isCenterline`);
        }
        if (value.isAuxiliaryObject !== undefined) {
            assertBoolean(value.isAuxiliaryObject, `${path}.isAuxiliaryObject`);
        }
        const record: SketchPersistentLineRecord = {
            entityId: value.entityId,
            kind: 'line',
            startPointId: assertPoolPointId(value.startPointId, `${path}.startPointId`),
            endPointId: assertPoolPointId(value.endPointId, `${path}.endPointId`),
            isConstruction: value.isConstruction,
            isInfiniteLength: value.isInfiniteLength,
            ...(value.isCenterline === true ? {isCenterline: true} : {}),
            ...(value.isAuxiliaryObject === true ? {isAuxiliaryObject: true} : {}),
        };
        return record;
    }

    if (value.kind === 'circle') {
        assertVector3Tuple(value.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(value.radius, `${path}.radius`);
        assertNonEmptyString(value.centerPointId, `${path}.centerPointId`);
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        if (value.radius <= 0) {
            throw new Error(`草图持久化文档圆半径必须为正数: ${path}.radius`);
        }
        const record: SketchPersistentCircleRecord = {
            entityId: value.entityId,
            kind: 'circle',
            centerWorld: [...value.centerWorld] as unknown as SketchPersistentVector3,
            radius: value.radius,
            centerPointId: value.centerPointId,
            isConstruction: value.isConstruction,
        };
        return record;
    }

    if (value.kind === 'arc') {
        assertVector3Tuple(value.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(value.radius, `${path}.radius`);
        assertFiniteNumber(value.startAngle, `${path}.startAngle`);
        assertFiniteNumber(value.endAngle, `${path}.endAngle`);
        assertBoolean(value.clockwise, `${path}.clockwise`);
        assertFiniteNumber(value.segments, `${path}.segments`);
        assertNonEmptyString(value.centerPointId, `${path}.centerPointId`);
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        if (value.radius <= 0) {
            throw new Error(`草图持久化文档圆弧半径必须为正数: ${path}.radius`);
        }
        const record: SketchPersistentArcRecord = {
            entityId: value.entityId,
            kind: 'arc',
            centerWorld: [...value.centerWorld] as unknown as SketchPersistentVector3,
            radius: value.radius,
            startAngle: value.startAngle,
            endAngle: value.endAngle,
            clockwise: value.clockwise,
            segments: value.segments,
            startPointId: assertPoolPointId(value.startPointId, `${path}.startPointId`),
            endPointId: assertPoolPointId(value.endPointId, `${path}.endPointId`),
            centerPointId: value.centerPointId,
            isConstruction: value.isConstruction,
        };
        return record;
    }

    if (value.kind === 'ellipse') {
        assertVector3Tuple(value.centerWorld, `${path}.centerWorld`);
        assertFiniteNumber(value.radiusX, `${path}.radiusX`);
        assertFiniteNumber(value.radiusY, `${path}.radiusY`);
        assertFiniteNumber(value.rotation, `${path}.rotation`);
        assertFiniteNumber(value.startAngle, `${path}.startAngle`);
        assertFiniteNumber(value.endAngle, `${path}.endAngle`);
        assertBoolean(value.clockwise, `${path}.clockwise`);
        assertFiniteNumber(value.segments, `${path}.segments`);
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        assertBoolean(value.hasContour, `${path}.hasContour`);
        if (value.radiusX <= 0 || value.radiusY <= 0) {
            throw new Error(`草图持久化文档椭圆半径必须为正数: ${path}`);
        }
        if (!Array.isArray(value.cardinalControlPoints)) {
            throw new Error(`草图持久化文档字段必须是数组: ${path}.cardinalControlPoints`);
        }
        const cardinalControlPoints = value.cardinalControlPoints.map((item, index) => {
            if (!isRecord(item)) {
                throw new Error(`草图持久化文档椭圆基准轴控制点必须是对象: ${path}.cardinalControlPoints[${index}]`);
            }
            assertNonEmptyString(item.role, `${path}.cardinalControlPoints[${index}].role`);
            return {
                role: item.role,
                pointId: assertPoolPointId(item.pointId, `${path}.cardinalControlPoints[${index}].pointId`),
            };
        });
        const record: SketchPersistentEllipseRecord = {
            entityId: value.entityId,
            kind: 'ellipse',
            centerWorld: [...value.centerWorld] as unknown as SketchPersistentVector3,
            radiusX: value.radiusX,
            radiusY: value.radiusY,
            rotation: value.rotation,
            startAngle: value.startAngle,
            endAngle: value.endAngle,
            clockwise: value.clockwise,
            segments: value.segments,
            startPointId: assertPoolPointId(value.startPointId, `${path}.startPointId`),
            endPointId: assertPoolPointId(value.endPointId, `${path}.endPointId`),
            centerPointId: assertPoolPointId(value.centerPointId, `${path}.centerPointId`),
            cardinalControlPoints,
            isConstruction: value.isConstruction,
            hasContour: value.hasContour,
        };
        return record;
    }

    if (value.kind === 'parabola') {
        assertVector3Tuple(value.focalWorld, `${path}.focalWorld`);
        assertVector3Tuple(value.apexWorld, `${path}.apexWorld`);
        assertFiniteNumber(value.startParameter, `${path}.startParameter`);
        assertFiniteNumber(value.endParameter, `${path}.endParameter`);
        assertFiniteNumber(value.segments, `${path}.segments`);
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        const record: SketchPersistentParabolaRecord = {
            entityId: value.entityId,
            kind: 'parabola',
            focalWorld: [...value.focalWorld] as unknown as SketchPersistentVector3,
            apexWorld: [...value.apexWorld] as unknown as SketchPersistentVector3,
            startParameter: value.startParameter,
            endParameter: value.endParameter,
            segments: value.segments,
            focalPointId: assertPoolPointId(value.focalPointId, `${path}.focalPointId`),
            apexPointId: assertPoolPointId(value.apexPointId, `${path}.apexPointId`),
            startPointId: assertPoolPointId(value.startPointId, `${path}.startPointId`),
            endPointId: assertPoolPointId(value.endPointId, `${path}.endPointId`),
            isConstruction: value.isConstruction,
        };
        return record;
    }

    if (value.kind === 'spline') {
        if (value.variant !== 'standard' && value.variant !== 'style') {
            throw new Error(`草图持久化文档样条变体无效: ${path}.variant=${String(value.variant)}`);
        }
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        if (!Array.isArray(value.controlPointIds) || value.controlPointIds.length < 2) {
            throw new Error(`草图持久化文档样条控制点至少需要 2 个: ${path}.controlPointIds`);
        }
        const controlPointIds = value.controlPointIds.map((pointId, index) => (
            assertPoolPointId(pointId, `${path}.controlPointIds[${index}]`)
        ));
        const record: SketchPersistentSplineRecord = {
            entityId: value.entityId,
            kind: 'spline',
            variant: value.variant,
            controlPointIds,
            curvePoints: parseVector3TupleList(value.curvePoints, `${path}.curvePoints`),
            curveMetadata: parseSketchSplineMetadataRecord(value.curveMetadata, `${path}.curveMetadata`),
            curveEvaluationDefinition: value.curveEvaluationDefinition === undefined
                ? undefined
                : parseSketchSplineEvaluationDefinition(
                    value.curveEvaluationDefinition,
                    `${path}.curveEvaluationDefinition`,
                ),
            isConstruction: value.isConstruction,
        };
        return record;
    }

    if (value.kind === 'slot') {
        assertNonEmptyString(value.mode, `${path}.mode`);
        if (value.slotKind !== 'straight' && value.slotKind !== 'arc') {
            throw new Error(`草图持久化文档槽口变体无效: ${path}.slotKind=${String(value.slotKind)}`);
        }
        assertBoolean(value.isConstruction, `${path}.isConstruction`);
        assertBoolean(value.hasContour, `${path}.hasContour`);
        assertNonEmptyString(value.startCapArcId, `${path}.startCapArcId`);
        assertNonEmptyString(value.endCapArcId, `${path}.endCapArcId`);
        if (!isRecord(value.junctionPointIds)) {
            throw new Error(`草图持久化文档槽口接合点必须是对象: ${path}.junctionPointIds`);
        }
        const junctionPointIds = {
            p1: assertPoolPointId(value.junctionPointIds.p1, `${path}.junctionPointIds.p1`),
            p2: assertPoolPointId(value.junctionPointIds.p2, `${path}.junctionPointIds.p2`),
            p3: assertPoolPointId(value.junctionPointIds.p3, `${path}.junctionPointIds.p3`),
            p4: assertPoolPointId(value.junctionPointIds.p4, `${path}.junctionPointIds.p4`),
        };
        if (value.slotKind === 'straight') {
            assertNonEmptyString(value.topLineId, `${path}.topLineId`);
            assertNonEmptyString(value.bottomLineId, `${path}.bottomLineId`);
            assertNonEmptyString(value.constructionCenterlineId, `${path}.constructionCenterlineId`);
            const record: SketchPersistentSlotRecord = {
                entityId: value.entityId,
                kind: 'slot',
                mode: value.mode,
                slotKind: 'straight',
                startCapArcId: value.startCapArcId,
                endCapArcId: value.endCapArcId,
                topLineId: value.topLineId,
                bottomLineId: value.bottomLineId,
                constructionCenterlineId: value.constructionCenterlineId,
                centerlineStartPointId: assertPoolPointId(value.centerlineStartPointId, `${path}.centerlineStartPointId`),
                centerlineEndPointId: assertPoolPointId(value.centerlineEndPointId, `${path}.centerlineEndPointId`),
                centerPointId: assertPoolPointId(value.centerPointId, `${path}.centerPointId`),
                junctionPointIds,
                geometryData: parsePlainData(value.geometryData, `${path}.geometryData`),
                isConstruction: value.isConstruction,
                hasContour: value.hasContour,
            };
            return record;
        }
        assertNonEmptyString(value.outerArcId, `${path}.outerArcId`);
        assertNonEmptyString(value.innerArcId, `${path}.innerArcId`);
        // 旧文档可缺省中心线弧 ID：解析层只做可选校验，重建层负责按几何数据合成。
        // 断言函数的类型缩窄只作用于当前块，必须提取为局部变量才能在 record 构造中保持 string 类型。
        let constructionCenterlineArcId: string | undefined;
        if (value.constructionCenterlineArcId !== undefined) {
            assertNonEmptyString(value.constructionCenterlineArcId, `${path}.constructionCenterlineArcId`);
            constructionCenterlineArcId = value.constructionCenterlineArcId;
        }
        const record: SketchPersistentSlotRecord = {
            entityId: value.entityId,
            kind: 'slot',
            mode: value.mode,
            slotKind: 'arc',
            startCapArcId: value.startCapArcId,
            endCapArcId: value.endCapArcId,
            outerArcId: value.outerArcId,
            innerArcId: value.innerArcId,
            ...(constructionCenterlineArcId
                ? {constructionCenterlineArcId}
                : {}),
            arcCenterPointId: assertPoolPointId(value.arcCenterPointId, `${path}.arcCenterPointId`),
            centerArcMidpointId: assertPoolPointId(value.centerArcMidpointId, `${path}.centerArcMidpointId`),
            junctionPointIds,
            geometryData: parsePlainData(value.geometryData, `${path}.geometryData`),
            isConstruction: value.isConstruction,
            hasContour: value.hasContour,
        };
        return record;
    }

    if (value.kind === 'collection') {
        assertNonEmptyString(value.creationSource, `${path}.creationSource`);
        assertBoolean(value.hasContour, `${path}.hasContour`);
        const parseEntityIdList = (listValue: unknown, listPath: string): string[] => {
            if (!Array.isArray(listValue)) {
                throw new Error(`草图持久化文档字段必须是数组: ${listPath}`);
            }
            return listValue.map((entityId, index) => {
                assertNonEmptyString(entityId, `${listPath}[${index}]`);
                return entityId;
            });
        };
        const directChildPointIds = (() => {
            if (!Array.isArray(value.directChildPointIds)) {
                throw new Error(`草图持久化文档字段必须是数组: ${path}.directChildPointIds`);
            }
            return value.directChildPointIds.map((pointId, index) => (
                assertPoolPointId(pointId, `${path}.directChildPointIds[${index}]`)
            ));
        })();
        const record: SketchPersistentCollectionRecord = {
            entityId: value.entityId,
            kind: 'collection',
            creationSource: value.creationSource,
            sourceEntityIds: parseEntityIdList(value.sourceEntityIds, `${path}.sourceEntityIds`),
            directChildEntityIds: parseEntityIdList(value.directChildEntityIds, `${path}.directChildEntityIds`),
            directChildPointIds,
            geometryData: parsePlainData(value.geometryData, `${path}.geometryData`),
            hasContour: value.hasContour,
        };
        return record;
    }

    throw new Error(`草图持久化文档存在未知实体类型: ${path}.kind=${String(value.kind)}`);
}

/**
 * 校验样条曲线元数据记录。
 * @param value 字段值。
 * @param path 字段路径。
 * @returns 已校验的元数据记录。
 */
export function parseSketchSplineMetadataRecord(
    value: unknown,
    path: string,
): SketchPersistentSplineMetadataRecord {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档样条元数据必须是对象: ${path}`);
    }
    assertNonEmptyString(value.selectionKind, `${path}.selectionKind`);
    assertNonEmptyString(value.curveKind, `${path}.curveKind`);
    assertNonEmptyString(value.closureKind, `${path}.closureKind`);
    if (value.styleSplineMode !== null) {
        assertNonEmptyString(value.styleSplineMode, `${path}.styleSplineMode`);
    }
    if (value.degree !== null) {
        assertFiniteNumber(value.degree, `${path}.degree`);
    }
    if (!Array.isArray(value.weights) || value.weights.some((weight) => typeof weight !== 'number' || !Number.isFinite(weight))) {
        throw new Error(`草图持久化文档样条权重必须是有限数字数组: ${path}.weights`);
    }
    assertBoolean(value.isRational, `${path}.isRational`);
    return {
        selectionKind: value.selectionKind,
        curveKind: value.curveKind,
        closureKind: value.closureKind,
        styleSplineMode: value.styleSplineMode as string | null,
        degree: value.degree as number | null,
        weights: [...value.weights] as number[],
        isRational: value.isRational,
    };
}

/**
 * 校验容器（槽口/集合）成员引用完整性与独占性。
 * @param entities 已解析实体列表。
 */
function assertContainerMemberIntegrity(entities: readonly SketchPersistentEntityRecord[]): void {
    const entityById = new Map(entities.map((entity) => [entity.entityId, entity]));
    const memberOwnership = new Map<string, string>();

    const claimMember = (
        containerId: string,
        memberId: string,
        expectedKinds: readonly string[],
        pathLabel: string,
    ): void => {
        const member = entityById.get(memberId);
        if (!member) {
            throw new Error(`草图持久化文档容器引用了不存在的成员实体: ${pathLabel}=${memberId}`);
        }
        if (!expectedKinds.includes(member.kind)) {
            throw new Error(`草图持久化文档容器成员类型不匹配: ${pathLabel}=${memberId}（实际 ${member.kind}）`);
        }
        const existingOwner = memberOwnership.get(memberId);
        if (existingOwner && existingOwner !== containerId) {
            throw new Error(`草图持久化文档成员实体被多个容器引用: ${memberId}`);
        }
        memberOwnership.set(memberId, containerId);
    };

    entities.forEach((entity) => {
        if (entity.kind === 'slot') {
            claimMember(entity.entityId, entity.startCapArcId, ['arc'], 'slot.startCapArcId');
            claimMember(entity.entityId, entity.endCapArcId, ['arc'], 'slot.endCapArcId');
            if (entity.slotKind === 'straight') {
                claimMember(entity.entityId, entity.topLineId!, ['line'], 'slot.topLineId');
                claimMember(entity.entityId, entity.bottomLineId!, ['line'], 'slot.bottomLineId');
                claimMember(entity.entityId, entity.constructionCenterlineId!, ['line'], 'slot.constructionCenterlineId');
            } else {
                claimMember(entity.entityId, entity.outerArcId!, ['arc'], 'slot.outerArcId');
                claimMember(entity.entityId, entity.innerArcId!, ['arc'], 'slot.innerArcId');
                // 旧文档可缺省：缺省时由重建层合成，不参与成员完整性校验。
                if (entity.constructionCenterlineArcId) {
                    claimMember(entity.entityId, entity.constructionCenterlineArcId, ['arc'], 'slot.constructionCenterlineArcId');
                }
            }
            return;
        }
        if (entity.kind === 'collection') {
            entity.sourceEntityIds.forEach((memberId) => {
                claimMember(entity.entityId, memberId, ['line', 'arc', 'ellipse', 'parabola', 'spline'], 'collection.sourceEntityIds');
            });
            entity.directChildEntityIds.forEach((memberId) => {
                claimMember(entity.entityId, memberId, ['line', 'arc', 'circle', 'ellipse', 'parabola', 'spline'], 'collection.directChildEntityIds');
            });
        }
    });
}

/**
 * 校验单条关系记录。
 * @param value 字段值。
 * @param path 字段路径。
 * @param resolvableIdSet 可解析目标 ID 集合（点池 + 实体 + 私有圆心点）。
 * @returns 已校验的关系记录。
 */
function parseRelationRecord(
    value: unknown,
    path: string,
    resolvableIdSet: ReadonlySet<string>,
): SketchPersistentRelationRecord {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档关系记录必须是对象: ${path}`);
    }
    assertNonEmptyString(value.constraintId, `${path}.constraintId`);
    assertNonEmptyString(value.relationKind, `${path}.relationKind`);
    const relationKind = normalizePersistentRelationKind(value.relationKind, `${path}.relationKind`);
    if (!Array.isArray(value.targetIds) || value.targetIds.length === 0) {
        throw new Error(`草图持久化文档关系目标必须是非空数组: ${path}.targetIds`);
    }
    const targetIds = value.targetIds.map((targetId, index) => {
        assertNonEmptyString(targetId, `${path}.targetIds[${index}]`);
        if (!resolvableIdSet.has(targetId)) {
            throw new Error(`草图持久化文档关系引用了文档内不存在的目标: ${path}.targetIds[${index}]=${targetId}`);
        }
        return targetId;
    });
    if (value.relationKind === SketchRelationKind.Symmetric) {
        if (targetIds.length !== 3) {
            throw new Error(`草图持久化文档 Symmetric 关系必须恰好包含镜像轴、源实体和镜像实体三个目标: ${path}.targetIds`);
        }
        assertUniqueIds(targetIds, `${path}.targetIds`);
    }
    if (value.numericValue !== null && value.numericValue !== undefined) {
        assertFiniteNumber(value.numericValue, `${path}.numericValue`);
    }
    if (value.suppressed !== undefined && typeof value.suppressed !== 'boolean') {
        throw new Error(`草图持久化文档关系 suppressed 必须是布尔值: ${path}.suppressed`);
    }
    const formulaPayload = value.formulaPayload === undefined || value.formulaPayload === null
        ? null
        : parsePlainData(value.formulaPayload, `${path}.formulaPayload`);
    if (relationKind === SketchRelationKind.Patterned) {
        const rawInstances = formulaPayload
            && typeof formulaPayload === 'object'
            && !Array.isArray(formulaPayload)
            ? Reflect.get(formulaPayload, 'instances')
            : null;
        const rawSeedTargetKinds = formulaPayload
            && typeof formulaPayload === 'object'
            && !Array.isArray(formulaPayload)
            ? Reflect.get(formulaPayload, 'seedTargetKinds')
            : null;
        const hasCenterPointReference = formulaPayload
            && typeof formulaPayload === 'object'
            && !Array.isArray(formulaPayload)
            ? Reflect.get(formulaPayload, 'hasCenterPointReference') !== false
            : true;
        const seedTargetCount = Array.isArray(rawSeedTargetKinds) && rawSeedTargetKinds.length > 0
            ? rawSeedTargetKinds.length
            : 1;
        const expectedTargetCount = (hasCenterPointReference ? 1 : 0)
            + seedTargetCount * ((Array.isArray(rawInstances) ? rawInstances.length : 0) + 1);
        if (!Array.isArray(rawInstances) || rawInstances.length === 0 || targetIds.length !== expectedTargetCount) {
            throw new Error(`草图持久化文档圆周阵列实例参数与派生目标数量不一致: ${path}`);
        }
    }
    return {
        constraintId: value.constraintId,
        relationKind,
        targetIds,
        numericValue: typeof value.numericValue === 'number' ? value.numericValue : null,
        suppressed: value.suppressed === true,
        branchHint: value.branchHint === undefined || value.branchHint === null
            ? null
            : parsePlainData(value.branchHint, `${path}.branchHint`),
        // 缺省字段必须继续缺省；解析器不能把旧文档的省略语义扩写成显式 null，确保合法文档解析幂等。
        ...(value.formulaPayload === undefined ? {} : {formulaPayload}),
    };
}

/**
 * 断言列表内持久 ID 不重复。
 * @param ids ID 列表。
 * @param label 错误上下文标签。
 */
function assertUniqueIds(ids: readonly string[], label: string): void {
    const seen = new Set<string>();
    ids.forEach((id) => {
        if (seen.has(id)) {
            throw new Error(`草图持久化文档存在重复 ${label}: ${id}`);
        }
        seen.add(id);
    });
}

/**
 * 校验可选的非空字符串字段。
 * @param value 字段值。
 * @param path JSON 字段路径。
 */
function assertOptionalNonEmptyString(value: unknown, path: string): void {
    if (value !== undefined) {
        assertNonEmptyString(value, path);
    }
}

/**
 * 校验统一 topology owner 描述。
 *
 * 导入装配 occurrence 由 `bodyBindingId` 收口在 owner 中；镜像轴不得再携带一套
 * 独立 assembly 引用协议，否则加载与 Convert Entities 会产生两种重绑定语义。
 *
 * @param value 待校验 owner。
 * @param path JSON 字段路径。
 */
function assertTopologyOwnerDescriptor(value: unknown, path: string): void {
    if (!isRecord(value)) {
        throw new Error(`草图持久化文档 topology owner 非法: ${path}`);
    }
    if (!['feature-result', 'model-body', 'imported-model-body'].includes(String(value.ownerKind))) {
        throw new Error(`草图持久化文档 topology ownerKind 非法: ${path}.ownerKind`);
    }
    assertNonEmptyString(value.ownerId, `${path}.ownerId`);
    assertOptionalNonEmptyString(value.resultObjectId, `${path}.resultObjectId`);
    assertOptionalNonEmptyString(value.bodyBindingId, `${path}.bodyBindingId`);
    assertOptionalNonEmptyString(value.modelBodyId, `${path}.modelBodyId`);
    assertOptionalNonEmptyString(value.modelBodyRevisionId, `${path}.modelBodyRevisionId`);
}

/**
 * 校验外部镜像轴稳定来源。
 * @param value 未知引用记录。
 * @param path JSON 字段路径。
 * @returns 深拷贝后的稳定引用。
 */
function parseMirrorAxisReference(value: unknown, path: string): SketchMirrorAxisReference {
    if (!isRecord(value) || value.schemaVersion !== 1) {
        throw new Error(`草图持久化文档镜像轴引用版本非法: ${path}`);
    }
    assertNonEmptyString(value.referenceId, `${path}.referenceId`);
    if (!isRecord(value.source)) {
        throw new Error(`草图持久化文档镜像轴来源必须是对象: ${path}.source`);
    }

    if (value.source.kind === 'datum-plane') {
        assertFiniteNumber(value.source.datumPlaneId, `${path}.source.datumPlaneId`);
        if (!Number.isInteger(value.source.datumPlaneId) || value.source.datumPlaneId < 0) {
            throw new Error(`草图持久化文档基准面 ID 必须是非负整数: ${path}.source.datumPlaneId`);
        }
    } else if (value.source.kind === 'topology-edge') {
        const descriptor = value.source.descriptor;
        if (!isRecord(descriptor) || descriptor.kind !== 'topology-edge') {
            throw new Error(`草图持久化文档模型边来源缺少 topology-edge 描述: ${path}.source.descriptor`);
        }
        assertTopologyOwnerDescriptor(descriptor.owner, `${path}.source.descriptor.owner`);
        if (!isRecord(descriptor.locator)) {
            throw new Error(`草图持久化文档模型边 locator 非法: ${path}.source.descriptor.locator`);
        }
        assertFiniteNumber(descriptor.locator.edgeIndex, `${path}.source.descriptor.locator.edgeIndex`);
        if (!Number.isInteger(descriptor.locator.edgeIndex) || descriptor.locator.edgeIndex < 0) {
            throw new Error(`草图持久化文档模型边索引必须是非负整数: ${path}.source.descriptor.locator.edgeIndex`);
        }
        if (descriptor.locator.curveKind !== 'line' || descriptor.locator.resolutionLevel !== 'exact') {
            throw new Error(`草图持久化文档镜像轴模型边必须是可精确解析的直线: ${path}.source.descriptor.locator`);
        }
        assertOptionalNonEmptyString(descriptor.locator.persistentRef, `${path}.source.descriptor.locator.persistentRef`);
        assertOptionalNonEmptyString(descriptor.locator.subEntityId, `${path}.source.descriptor.locator.subEntityId`);
        if (!descriptor.locator.persistentRef && !descriptor.locator.subEntityId) {
            throw new Error(`草图持久化文档镜像轴模型边缺少稳定拓扑身份: ${path}.source.descriptor.locator`);
        }
        // 镜像轴必须始终回到权威拓扑身份解析；接受世界坐标指纹会把失效引用静默绑定到近似边。
        if (descriptor.locator.geomFingerprint !== undefined) {
            throw new Error(`草图持久化文档镜像轴模型边禁止保存世界坐标指纹: ${path}.source.descriptor.locator.geomFingerprint`);
        }
    } else if (value.source.kind === 'planar-face') {
        const face = value.source.face;
        if (!isRecord(face)) {
            throw new Error(`草图持久化文档平面模型面来源非法: ${path}.source.face`);
        }
        assertTopologyOwnerDescriptor(face.owner, `${path}.source.face.owner`);
        if (!isRecord(face.locator)) {
            throw new Error(`草图持久化文档模型面 locator 非法: ${path}.source.face.locator`);
        }
        assertFiniteNumber(face.locator.faceIndex, `${path}.source.face.locator.faceIndex`);
        if (!Number.isInteger(face.locator.faceIndex) || face.locator.faceIndex < 0) {
            throw new Error(`草图持久化文档模型面索引必须是非负整数: ${path}.source.face.locator.faceIndex`);
        }
        assertOptionalNonEmptyString(face.locator.semanticName, `${path}.source.face.locator.semanticName`);
        assertOptionalNonEmptyString(face.locator.persistentRef, `${path}.source.face.locator.persistentRef`);
        assertOptionalNonEmptyString(face.locator.subEntityId, `${path}.source.face.locator.subEntityId`);
        if (
            !face.locator.semanticName
            && !face.locator.persistentRef
            && !face.locator.subEntityId
        ) {
            throw new Error(`草图持久化文档镜像轴模型面缺少稳定拓扑身份: ${path}.source.face.locator`);
        }
        assertOptionalNonEmptyString(face.locator.surfaceKind, `${path}.source.face.locator.surfaceKind`);
        if (face.locator.owningFeatureId !== null) {
            assertOptionalNonEmptyString(face.locator.owningFeatureId, `${path}.source.face.locator.owningFeatureId`);
        }
    } else {
        throw new Error(`草图持久化文档镜像轴来源类型非法: ${path}.source.kind`);
    }

    if (!isSerializablePlainData(value)) {
        throw new Error(`草图持久化文档镜像轴引用必须是纯 JSON 数据: ${path}`);
    }
    return JSON.parse(JSON.stringify(value)) as SketchMirrorAxisReference;
}

/**
 * 解析 v1 可选的外部镜像轴目标记录。
 * @param value 文档字段值。
 * @returns 按 targetId 排序的记录；缺省或空数组返回 null。
 */
function parseMirrorAxisReferenceRecords(
    value: unknown,
): readonly SketchPersistentMirrorAxisReferenceRecord[] | null {
    if (value === undefined) {
        return null;
    }
    if (!Array.isArray(value)) {
        throw new Error('草图持久化文档字段必须是数组: mirrorAxisReferences');
    }
    const records = value.map((item, index) => {
        if (!isRecord(item)) {
            throw new Error(`草图持久化文档镜像轴目标必须是对象: mirrorAxisReferences[${index}]`);
        }
        assertNonEmptyString(item.targetId, `mirrorAxisReferences[${index}].targetId`);
        return {
            targetId: item.targetId,
            reference: parseMirrorAxisReference(
                item.reference,
                `mirrorAxisReferences[${index}].reference`,
            ),
        };
    });
    assertUniqueIds(records.map((record) => record.targetId), 'mirrorAxisReferences.targetId');
    assertUniqueIds(records.map((record) => record.reference.referenceId), 'mirrorAxisReferences.reference.referenceId');
    return records.length > 0
        ? records.sort((left, right) => left.targetId.localeCompare(right.targetId))
        : null;
}

/**
 * 校验外部镜像轴目标只作为实体级对称关系的首目标存在。
 *
 * provider 不是独立草图几何；没有关系的记录会在加载后污染 SketchGroup，而把它用于其它关系或
 * 非首目标会破坏实体级对称关系固定的“轴、源、镜像”三元顺序，因此解析阶段必须显式拒绝。
 *
 * @param records 外部镜像轴记录。
 * @param relations 已完成目标存在性校验的关系列表。
 */
function assertMirrorAxisReferenceRelationIntegrity(
    records: readonly SketchPersistentMirrorAxisReferenceRecord[] | null,
    relations: readonly SketchPersistentRelationRecord[],
): void {
    if (!records) {
        return;
    }
    const providerTargetIds = new Set(records.map((record) => record.targetId));
    const validUsageCountMap = new Map(records.map((record) => [record.targetId, 0]));

    relations.forEach((relation) => {
        relation.targetIds.forEach((targetId, targetIndex) => {
            if (!providerTargetIds.has(targetId)) {
                return;
            }
            if (
                relation.relationKind !== SketchRelationKind.Symmetric
                || relation.targetIds.length !== 3
                || targetIndex !== 0
            ) {
                throw new Error(
                    `草图持久化文档镜像轴目标只能作为三目标 Symmetric 关系的第一目标: `
                    + `${relation.constraintId}.targetIds[${targetIndex}]=${targetId}`,
                );
            }
            validUsageCountMap.set(targetId, (validUsageCountMap.get(targetId) ?? 0) + 1);
        });
    });

    records.forEach((record) => {
        if ((validUsageCountMap.get(record.targetId) ?? 0) === 0) {
            throw new Error(`草图持久化文档存在未被实体对称关系引用的孤儿镜像轴目标: ${record.targetId}`);
        }
    });
}

//------ 草图尺寸持久化 开始------
/**
 * 校验并解析可选尺寸记录列表。
 *
 * 设计说明：
 * 1. 深校验直接复用尺寸聚合构造（`SketchDimension.fromSnapshot`）：值域、量纲、
 *    引用结构等不变量与运行时完全一致，解析失败带 JSON 路径抛错，不静默降级；
 * 2. 引用目标不强制在当前文档可解析——悬空尺寸（dangling + repair ghost）
 *    允许持久化，加载后保持悬空等待 Reattach（02 计划 §9.2）；
 * 3. 输出按 dimensionId 字典序，保证确定性序列化往返一致。
 *
 * @param value 未知 JSON 值（可省略）。
 * @param sketchId 所属草图持久 ID。
 * @returns 尺寸快照列表；字段省略或为空时返回 null。
 */
function parseDimensionRecords(
    value: unknown,
    sketchId: string,
): readonly SketchDimensionSnapshot[] | null {
    if (value === undefined) {
        return null;
    }
    if (!Array.isArray(value)) {
        throw new Error('草图持久化文档字段必须是数组: dimensions');
    }
    if (value.length === 0) {
        return null;
    }

    const dimensions = value.map((item, index) => {
        try {
            const snapshot = SketchDimension.fromSnapshot(item as SketchDimensionSnapshot).toSnapshot();

            if (snapshot.sketchId !== sketchId) {
                throw new Error(`sketchId ${snapshot.sketchId} 与文档 ${sketchId} 不一致`);
            }

            return snapshot;
        } catch (error) {
            throw new Error(
                `草图持久化文档 dimensions[${index}] 非法: `
                + `${String(error instanceof Error ? error.message : error)}`,
            );
        }
    });

    assertUniqueIds(dimensions.map((dimension) => dimension.dimensionId), 'dimensions.dimensionId');

    return [...dimensions].sort((left, right) => left.dimensionId.localeCompare(right.dimensionId));
}
//------ 草图尺寸持久化 结束------

/**
 * 解析圆周草图阵列持久化记录。
 *
 * @param value 原始字段值。
 * @param resolvableIdSet 当前文档可解析的草图对象持久 ID。
 * @returns 已校验并排序的圆周阵列记录；字段缺省时返回 null。
 */
function parseCircularPatternRecords(
    value: unknown,
    resolvableIdSet: ReadonlySet<string>,
): readonly SketchPersistentCircularPatternRecord[] | null {
    if (value === undefined) {
        return null;
    }
    if (!Array.isArray(value)) {
        throw new Error('草图持久化文档字段必须是数组: circularPatterns');
    }

    const patterns = value.map((item, patternIndex): SketchPersistentCircularPatternRecord => {
        const path = `circularPatterns[${patternIndex}]`;
        if (!isRecord(item) || item.kind !== 'circular-pattern') {
            throw new Error(`草图持久化文档圆周阵列记录非法: ${path}`);
        }
        assertNonEmptyString(item.persistentId, `${path}.persistentId`);
        if (!Array.isArray(item.sourceEntityPersistentIds) || item.sourceEntityPersistentIds.length === 0) {
            throw new Error(`草图持久化文档圆周阵列源实体不能为空: ${path}.sourceEntityPersistentIds`);
        }
        item.sourceEntityPersistentIds.forEach((persistentId, index) => {
            assertNonEmptyString(persistentId, `${path}.sourceEntityPersistentIds[${index}]`);
            if (!resolvableIdSet.has(persistentId)) {
                throw new Error(`草图持久化文档圆周阵列引用未知源实体: ${persistentId}`);
            }
        });
        if (!Array.isArray(item.center2d) || item.center2d.length !== 2) {
            throw new Error(`草图持久化文档圆周阵列中心必须是二维坐标: ${path}.center2d`);
        }
        assertFiniteNumber(item.center2d[0], `${path}.center2d[0]`);
        assertFiniteNumber(item.center2d[1], `${path}.center2d[1]`);
        assertFiniteNumber(item.radius, `${path}.radius`);
        assertFiniteNumber(item.startAngleRad, `${path}.startAngleRad`);
        assertFiniteNumber(item.totalAngleRad, `${path}.totalAngleRad`);
        assertFiniteNumber(item.spacingAngleRad, `${path}.spacingAngleRad`);
        assertBoolean(item.equalSpacing, `${path}.equalSpacing`);
        assertBoolean(item.reverseDirection, `${path}.reverseDirection`);
        assertBoolean(item.rotateInstances, `${path}.rotateInstances`);
        assertBoolean(item.showRadiusDimension, `${path}.showRadiusDimension`);
        assertBoolean(item.showSpacingDimension, `${path}.showSpacingDimension`);
        assertBoolean(item.showInstanceCount, `${path}.showInstanceCount`);
        // 回调闭包只读取已校验的局部数值，避免 Record 属性在闭包内退化回 unknown。
        const instanceCount = item.instanceCount;
        assertFiniteNumber(instanceCount, `${path}.instanceCount`);
        if (!Number.isInteger(instanceCount) || instanceCount < 2) {
            throw new Error(`草图持久化文档圆周阵列实例数非法: ${path}.instanceCount`);
        }
        if (!Array.isArray(item.skippedInstanceIndexes)) {
            throw new Error(`草图持久化文档圆周阵列跳过实例必须是数组: ${path}.skippedInstanceIndexes`);
        }
        const skippedInstanceIndexes = item.skippedInstanceIndexes.map((instanceIndex, index) => {
            assertFiniteNumber(instanceIndex, `${path}.skippedInstanceIndexes[${index}]`);
            if (!Number.isInteger(instanceIndex) || instanceIndex <= 0 || instanceIndex >= instanceCount) {
                throw new Error(`草图持久化文档圆周阵列跳过实例索引非法: ${instanceIndex}`);
            }
            return instanceIndex;
        });
        if (!Array.isArray(item.instances) || item.instances.length === 0) {
            throw new Error(`草图持久化文档圆周阵列实例记录不能为空: ${path}.instances`);
        }
        const instances = item.instances.map((instance, instanceRecordIndex) => {
            const instancePath = `${path}.instances[${instanceRecordIndex}]`;
            if (!isRecord(instance)) {
                throw new Error(`草图持久化文档圆周阵列实例记录非法: ${instancePath}`);
            }
            assertFiniteNumber(instance.instanceIndex, `${instancePath}.instanceIndex`);
            if (!Number.isInteger(instance.instanceIndex) || instance.instanceIndex < 0 || instance.instanceIndex >= instanceCount) {
                throw new Error(`草图持久化文档圆周阵列实例索引非法: ${instance.instanceIndex}`);
            }
            if (!Array.isArray(instance.entityPersistentIds) || instance.entityPersistentIds.length === 0) {
                throw new Error(`草图持久化文档圆周阵列实例实体不能为空: ${instancePath}.entityPersistentIds`);
            }
            const entityPersistentIds = instance.entityPersistentIds.map((persistentId, entityIndex) => {
                assertNonEmptyString(persistentId, `${instancePath}.entityPersistentIds[${entityIndex}]`);
                if (!resolvableIdSet.has(persistentId)) {
                    throw new Error(`草图持久化文档圆周阵列实例引用未知实体: ${persistentId}`);
                }
                return persistentId;
            });
            return {
                instanceIndex: instance.instanceIndex,
                entityPersistentIds,
            };
        });

        return {
            kind: 'circular-pattern',
            persistentId: item.persistentId,
            sourceEntityPersistentIds: [...item.sourceEntityPersistentIds] as string[],
            center2d: [item.center2d[0], item.center2d[1]],
            radius: item.radius,
            startAngleRad: item.startAngleRad,
            totalAngleRad: item.totalAngleRad,
            equalSpacing: item.equalSpacing,
            spacingAngleRad: item.spacingAngleRad,
            reverseDirection: item.reverseDirection,
            instanceCount,
            rotateInstances: item.rotateInstances,
            showRadiusDimension: item.showRadiusDimension,
            showSpacingDimension: item.showSpacingDimension,
            showInstanceCount: item.showInstanceCount,
            skippedInstanceIndexes,
            instances,
        };
    });

    assertUniqueIds(patterns.map((pattern) => pattern.persistentId), 'circularPatterns.persistentId');
    return [...patterns].sort((left, right) => left.persistentId.localeCompare(right.persistentId));
}

/**
 * 解析并校验草图持久化文档。
 * @param value 未知 JSON 值。
 * @returns 已校验的草图持久化文档。
 * @throws 当输入不是支持版本的完整有效草图文档，或任一字段、标识与引用校验失败时抛出错误。
 */
export function parseSketchPersistentDocument(value: unknown): SketchPersistentDocument {
    if (!isRecord(value)) {
        throw new Error('草图持久化文档必须是对象');
    }
    if (value.schemaVersion !== SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1) {
        throw new Error(`不支持的草图持久化文档版本: ${String(value.schemaVersion)}`);
    }
    assertNonEmptyString(value.sketchId, 'sketchId');
    assertNonEmptyString(value.name, 'name');
    const referencePlane = parseReferencePlaneRecord(value.referencePlane, 'referencePlane');

    if (!Array.isArray(value.points)) {
        throw new Error('草图持久化文档字段必须是数组: points');
    }
    const points = value.points.map((item, index) => parsePointRecord(item, `points[${index}]`));
    assertUniqueIds(points.map((point) => point.pointId), 'points.pointId');
    const pointRecordMap = new Map(points.map((point) => [point.pointId, point]));
    const pointIdSet = new Set(pointRecordMap.keys());

    if (!Array.isArray(value.entities)) {
        throw new Error('草图持久化文档字段必须是数组: entities');
    }
    const entities = value.entities.map((item, index) => parseEntityRecord(item, `entities[${index}]`, pointIdSet));
    assertUniqueIds(entities.map((entity) => entity.entityId), 'entities.entityId');

    //------ 容器成员完整性 开始------
    assertContainerMemberIntegrity(entities);
    //------ 容器成员完整性 结束------

    /**
     * 圆/圆弧圆心采用“私有声明或点池引用”双态语义：
     * 1. 命中点池时允许多条曲线复用，但声明坐标必须与点池事实一致；
     * 2. 未命中点池时沿用旧文档语义，由单条曲线私有声明，ID 仍必须全文档唯一。
     */
    const centerPointRecords = entities
        .filter((entity): entity is SketchPersistentCircleRecord | SketchPersistentArcRecord => (
            entity.kind === 'circle' || entity.kind === 'arc'
        ));
    centerPointRecords.forEach((entity) => {
        const pooledCenterPoint = pointRecordMap.get(entity.centerPointId);
        if (
            pooledCenterPoint
            && pooledCenterPoint.position.some((coordinate, index) => coordinate !== entity.centerWorld[index])
        ) {
            throw new Error(
                `草图持久化文档圆心引用与点池坐标不一致: `
                + `entities.${entity.entityId}.centerPointId=${entity.centerPointId}`,
            );
        }
    });
    const privateCenterPointIds = centerPointRecords
        .map((entity) => entity.centerPointId)
        .filter((centerPointId) => !pointIdSet.has(centerPointId));
    assertUniqueIds(
        [...pointIdSet, ...entities.map((entity) => entity.entityId), ...privateCenterPointIds],
        '持久 ID（跨点池/实体/私有圆心点）',
    );
    const resolvableIdSet = new Set([
        ...pointIdSet,
        ...entities.map((entity) => entity.entityId),
        ...privateCenterPointIds,
    ]);

    const mirrorAxisReferences = parseMirrorAxisReferenceRecords(value.mirrorAxisReferences);
    mirrorAxisReferences?.forEach((record) => {
        if (resolvableIdSet.has(record.targetId)) {
            throw new Error(`草图持久化文档镜像轴目标与几何实体 ID 冲突: ${record.targetId}`);
        }
        // 外部 provider 以正式组内 persistentId 进入关系目标解析域。
        resolvableIdSet.add(record.targetId);
    });

    if (!Array.isArray(value.relations)) {
        throw new Error('草图持久化文档字段必须是数组: relations');
    }
    const relations = value.relations.map((item, index) => parseRelationRecord(item, `relations[${index}]`, resolvableIdSet));
    assertUniqueIds(relations.map((relation) => relation.constraintId), 'relations.constraintId');
    assertMirrorAxisReferenceRelationIntegrity(mirrorAxisReferences, relations);

    //------ 圆周草图阵列持久化 开始------
    const circularPatterns = parseCircularPatternRecords(value.circularPatterns, resolvableIdSet);
    const circularPatternIdSet = new Set(
        (circularPatterns ?? []).map((pattern) => pattern.persistentId),
    );
    relations.forEach((relation, relationIndex) => {
        if (relation.relationKind !== SketchRelationKind.Patterned) {
            return;
        }
        const formulaPayload = relation.formulaPayload;
        const patternPersistentId = formulaPayload
            && typeof formulaPayload === 'object'
            && !Array.isArray(formulaPayload)
            ? Reflect.get(formulaPayload, 'patternPersistentId')
            : null;
        if (typeof patternPersistentId !== 'string' || !circularPatternIdSet.has(patternPersistentId)) {
            throw new Error(
                `草图持久化文档阵列关系引用了不存在的圆周阵列：relations[${relationIndex}]`,
            );
        }
    });
    //------ 圆周草图阵列持久化 结束------

    //------ 草图尺寸持久化 开始------
    const dimensions = parseDimensionRecords(value.dimensions, value.sketchId);
    //------ 草图尺寸持久化 结束------

    return {
        schemaVersion: SKETCH_PERSISTENT_DOCUMENT_SCHEMA_VERSION_V1,
        sketchId: value.sketchId,
        name: value.name,
        referencePlane,
        points,
        entities,
        relations,
        ...(circularPatterns ? {circularPatterns} : {}),
        ...(mirrorAxisReferences ? {mirrorAxisReferences} : {}),
        ...(circularPatterns ? {circularPatterns} : {}),
        ...(dimensions ? {dimensions} : {}),
    };
}
